import { ChangeEvent, useEffect, useState } from 'react';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import {
  EMPTY_FORM_FIELD_ROW_ERRORS,
  FormFieldRowInputType,
  FormFieldRowInputTypeLabels,
  FormFieldRowRequirement,
  FormFieldRowValue,
  getFormFieldRowErrors,
  hasFormFieldRowErrors,
} from '@components/form-field-row';
import { Input } from '@components/input';
import { InputRadio } from '@components/input-radio';
import { ConfirmModalV2 } from '@components/modals/confirm-modal-v2';
import { ModalV2 } from '@components/modals/modal-v2';
import { PillToggleVariant, SIZE_L } from '@components/pill-toggle';
import { PillToggleGroup, PillToggleGroupOption } from '@components/pill-toggle-group';

import {
  FormFieldTableDeleteCopy,
  FormFieldTableMobileModalCopy,
  FormFieldTableRequirementLabels,
  FormFieldTableRow,
} from '../form-field-table.type';

const REQUIREMENT_VARIANT: Record<FormFieldRowRequirement, PillToggleVariant> = {
  off: 'critical',
  optional: 'warning',
  required: 'success',
};

const AVAILABLE_TYPES: FormFieldRowInputType[] = ['text', 'number'];

interface FormFieldTableMobileModalProps {
  row: FormFieldTableRow | null;
  onClose: () => void;
  onRequirementChange: (id: string, next: FormFieldRowRequirement) => void;
  onCustomFieldSave: (id: string, next: FormFieldRowValue) => void;
  onCustomFieldDelete: (id: string) => void;
  requirementLabels: FormFieldTableRequirementLabels;
  typeLabels: FormFieldRowInputTypeLabels;
  namePlaceholder: string;
  minPlaceholder: string;
  maxPlaceholder: string;
  saveLabel: string;
  deleteConfirm: FormFieldTableDeleteCopy;
  modalZIndex?: number;
  modalSurfaceClassName?: string;
  copy: FormFieldTableMobileModalCopy;
  dataTestId: string;
}

export const FormFieldTableMobileModal = ({
  row,
  onClose,
  onRequirementChange,
  onCustomFieldSave,
  onCustomFieldDelete,
  requirementLabels,
  typeLabels,
  namePlaceholder,
  minPlaceholder,
  maxPlaceholder,
  saveLabel,
  deleteConfirm,
  modalZIndex,
  modalSurfaceClassName,
  copy,
  dataTestId,
}: FormFieldTableMobileModalProps) => {
  const isOpen = row !== null && row.kind !== 'locked';

  const [draftRequirement, setDraftRequirement] = useState<FormFieldRowRequirement>('optional');
  const [draftValue, setDraftValue] = useState<FormFieldRowValue>({ type: 'text', name: '' });
  const [errors, setErrors] = useState(EMPTY_FORM_FIELD_ROW_ERRORS);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

  useEffect(() => {
    if (!row) return;
    setDraftRequirement(row.requirement);
    setErrors(EMPTY_FORM_FIELD_ROW_ERRORS);
    if (row.kind === 'custom') setDraftValue(row.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  if (!row) return null;

  const updateDraft = (next: FormFieldRowValue) => {
    setDraftValue(next);
    setErrors(EMPTY_FORM_FIELD_ROW_ERRORS);
  };

  const handleConfirm = () => {
    if (row.kind === 'custom') {
      const nextErrors = getFormFieldRowErrors(draftValue);
      if (hasFormFieldRowErrors(nextErrors)) {
        setErrors(nextErrors);
        return;
      }
      onCustomFieldSave(row.id, draftValue);
    }
    onRequirementChange(row.id, draftRequirement);
    onClose();
  };

  const handleBackdropClose = () => {
    if (row.kind === 'custom' && row.value.name.trim() === '') {
      setIsDeleteConfirming(true);
      return;
    }
    onClose();
  };

  const handleDeleteRequest = () => setIsDeleteConfirming(true);
  const handleDeleteCancel = () => setIsDeleteConfirming(false);
  const handleDeleteConfirm = () => {
    if (row.kind !== 'custom') return;
    onCustomFieldDelete(row.id);
    setIsDeleteConfirming(false);
    onClose();
  };

  const resolvedTypeLabels: Required<FormFieldRowInputTypeLabels> = {
    text: typeLabels.text ?? 'Text',
    number: typeLabels.number ?? 'Number',
  };

  const handleTypeChange = (next: FormFieldRowInputType) => {
    if (row.kind !== 'custom' || next === draftValue.type) return;
    updateDraft(
      next === 'text'
        ? { type: 'text', name: draftValue.name }
        : { type: 'number', name: draftValue.name, min: '', max: '' }
    );
  };

  const requirementOptions: PillToggleGroupOption<FormFieldRowRequirement>[] = [
    { value: 'off', label: requirementLabels.off, variant: REQUIREMENT_VARIANT.off },
    { value: 'optional', label: requirementLabels.optional, variant: REQUIREMENT_VARIANT.optional },
    { value: 'required', label: requirementLabels.required, variant: REQUIREMENT_VARIANT.required },
  ];

  const title = row.kind === 'custom' ? copy.customTitle : row.label;

  return (
    <>
      <ModalV2
        isOpen={isOpen}
        onClose={handleBackdropClose}
        variant='sm'
        ariaLabel={title}
        surfaceClassName={modalSurfaceClassName}
        zIndex={modalZIndex}>
        <div className='flex flex-col bg-primary' data-testid={dataTestId}>
          <div className='flex flex-col gap-4 px-4 pb-4'>
            <h2 className='text-headline-s text-primary py-4'>{title}</h2>

            {row.kind === 'custom' && (
              <>
                <div className='flex flex-col gap-2'>
                  <span className='text-body-s text-secondary'>{copy.nameLabel}</span>
                  <Input
                    type='text'
                    value={draftValue.name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      updateDraft({ ...draftValue, name: event.target.value })
                    }
                    placeholder={namePlaceholder}
                    hasError={errors.name}
                    data-testid={`${dataTestId}-name`}
                  />
                </div>

                {draftValue.type === 'number' && (
                  <div className='flex items-center gap-2'>
                    <Input
                      type='number'
                      value={draftValue.min}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        updateDraft({ ...draftValue, min: event.target.value })
                      }
                      placeholder={minPlaceholder}
                      hasError={errors.min}
                      data-testid={`${dataTestId}-min`}
                    />
                    <Input
                      type='number'
                      value={draftValue.max}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        updateDraft({ ...draftValue, max: event.target.value })
                      }
                      placeholder={maxPlaceholder}
                      hasError={errors.max}
                      data-testid={`${dataTestId}-max`}
                    />
                  </div>
                )}

                <div className='flex flex-col gap-3'>
                  <span className='text-body-s text-secondary'>{copy.typeLabel}</span>
                  {AVAILABLE_TYPES.map((type) => (
                    <InputRadio
                      key={type}
                      name={`${dataTestId}-type`}
                      value={type}
                      checked={draftValue.type === type}
                      onChange={() => handleTypeChange(type)}
                      label={resolvedTypeLabels[type]}
                      data-testid={`${dataTestId}-type-${type}`}
                    />
                  ))}
                </div>
              </>
            )}

            <PillToggleGroup<FormFieldRowRequirement>
              value={draftRequirement}
              onChange={setDraftRequirement}
              options={requirementOptions}
              size={SIZE_L}
              fullWidth
              ariaLabel={requirementLabels.required}
            />
          </div>

          <div className='flex w-full items-center gap-3 border-t border-extra-soft px-4 pt-4 pb-6'>
            {row.kind === 'custom' && (
              <ButtonSecondary onClick={handleDeleteRequest} className='flex-1' data-testid={`${dataTestId}-delete`}>
                {copy.deleteFieldLabel}
              </ButtonSecondary>
            )}
            <ButtonPrimary onClick={handleConfirm} className='flex-1' data-testid={`${dataTestId}-confirm`}>
              {saveLabel}
            </ButtonPrimary>
          </div>
        </div>
      </ModalV2>

      {row.kind === 'custom' && (
        <ConfirmModalV2
          isOpen={isDeleteConfirming}
          type='critical'
          title={deleteConfirm.title}
          description={deleteConfirm.description}
          confirmText={deleteConfirm.confirmText}
          cancelText={deleteConfirm.cancelText}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          surfaceClassName={modalSurfaceClassName}
          zIndex={modalZIndex}
        />
      )}
    </>
  );
};
